import React, { useState, useEffect, useRef } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtCurrency, fmtCurrencyK, fmtPct } from "../lib/formatters";
import { useHoverIntent } from "../lib/DelayedTooltip";
import { useAutoDismiss } from "../hooks/useAutoDismiss";

// Click-based per-year detail popover. Dismisses on outside click.
function MixPopover({ segName, calc, horizon, onClose, anchor }) {
  const ref = useRef(null);
  // 2026-02-19 (Dave) — auto-dismiss after `hover_visible_seconds`
  // so this popover shares the same cadence as all other Dashboard
  // click-revealed popups.
  useAutoDismiss(true, onClose);
  useEffect(() => {
    const h = (e) => {
      if (!ref.current?.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  const slice = calc.projection.slice(0, horizon);
  const rows = slice.map((r) => {
    if (segName === "Reserves")
      return { year: r.year, v: (r.contribution || 0) + (r.interest || 0) };
    if (segName === "Special Assessments")
      return { year: r.year, v: r.special_assessment || 0 };
    // "Total" — center click
    return { year: r.year, v: r.expenditures || 0 };
  });
  const total = rows.reduce((s, r) => s + r.v, 0);
  const label =
    segName === "Total" ? "Total Spending" : segName;
  const note =
    segName === "Reserves"
      ? "Contributions + interest each year"
      : segName === "Special Assessments"
        ? "Years with a special assessment"
        : "Element replacement outflows each year";
  const accent = segName === "Special Assessments" ? "#EA6A47" : "#1C4E80";
  return (
    <div
      ref={ref}
      data-testid={`donut-popover-${segName.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white shadow-lg"
      style={{
        position: "absolute",
        zIndex: 40,
        left: anchor.x,
        top: anchor.y,
        border: "1px solid #1C4E80",
        borderRadius: 3,
        fontSize: 11,
        minWidth: 220,
        maxHeight: 320,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="bg-navy-wash border-b border-rule">
        <div className="px-2.5 py-1.5 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <div
                className="font-display font-semibold text-navy"
                style={{ fontSize: 12.5, letterSpacing: 0 }}
              >
                {label}
              </div>
              {/* 2026-02-19 (Dave) — Total sits on the SAME baseline as
                  the title, right-aligned inside the label column so
                  it's visually locked to the title row, not the
                  midpoint of title+subtitle. */}
              <div
                className="font-mono tabular-nums"
                style={{ fontWeight: 600, color: accent, fontSize: 11 }}
                data-testid="donut-popover-total-value"
              >
                {fmtCurrency(total)}
              </div>
            </div>
            <div className="text-slate" style={{ fontSize: 10.5 }}>
              {note}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate hover:text-navy px-1 self-start"
            data-testid="donut-popover-close"
          >
            ×
          </button>
        </div>
      </div>
      <div style={{ overflowY: "auto", maxHeight: 230 }}>
        {rows.length === 0 ? (
          <div className="px-2.5 py-2 text-slate" style={{ fontSize: 10.5 }}>
            No qualifying years.
          </div>
        ) : (() => {
          // Peak Year = row with the highest amount in this list. When the
          // whole list is empty (segment has no qualifying years) or all
          // values are zero, there is no peak — every row renders normally.
          const peakVal = rows.reduce((m, r) => (r.v > m ? r.v : m), 0);
          const hasPeak = peakVal > 0;
          return (
            <table className="w-full" style={{ fontFamily: "IBM Plex Mono", fontSize: 10.5 }}>
              <tbody>
                {rows.map((r) => {
                  const isPeak = hasPeak && r.v === peakVal;
                  return (
                    <tr key={r.year} className="border-b border-rule" data-testid={isPeak ? `donut-popover-peak-row-${r.year}` : undefined}>
                      <td
                        className="px-2 py-0.5 text-ink tabular-nums"
                        style={{ fontWeight: 700 }}
                      >
                        {r.year}
                      </td>
                      <td
                        className="px-1 py-0.5 text-slate text-center"
                        style={{
                          fontFamily: "Source Sans 3, ui-sans-serif, system-ui",
                          fontSize: 10,
                          fontStyle: "italic",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isPeak ? "Peak Year" : ""}
                      </td>
                      <td className="px-2 py-0.5 text-right tabular-nums text-ink">
                        {r.v === 0 ? "—" : fmtCurrency(r.v)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  );
}

// Panel — Funding Mix donut. Click a segment to reveal per-year detail.
export default function FundingMixDonut({ calc, association, horizon = 30 }) {
  const [active, setActive] = useState(null);
  const [hovering, setHovering] = useState(false);
  const hover = useHoverIntent();
  const anchorAt = (e) => {
    const parent = e.currentTarget.closest('[data-testid="funding-mix-panel"]');
    const rect = parent?.getBoundingClientRect();
    return { name: "Total", x: rect ? e.clientX - rect.left + 10 : 100, y: rect ? e.clientY - rect.top + 10 : 100 };
  };
  if (!calc || !association) return null;

  // Horizon-scoped totals: sum only the selected number of projection years so
  // the hole value, pie proportions and legend all reflect the current horizon.
  const slice = (calc.projection || []).slice(0, horizon);
  const S = calc.display?.horizons?.[String(slice.length)] || {};
  const total = S.total_expenditures || 0;
  const specials = S.total_special_assessments || 0;
  const reserves = S.reserves_spend || 0;

  const data = [
    { name: "Reserves", value: reserves, fill: "#88BFDE" },
    { name: "Special Assessments", value: specials, fill: "#EA6A47" },
  ];
  const pctReserves = S.pct_reserves || 0;
  const pctSpecials = S.pct_specials || 0;
  const perMember = S.specials_per_member || 0;

  return (
    <div className="panel-compact flex flex-col" style={{ height: "100%", minHeight: 0 }} data-testid="funding-mix-panel">
      <div className="dash-panel-header">
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
          Funding Mix
        </div>
        <div
          className="text-[10.5px] text-navy font-semibold font-mono tabular-nums"
          data-testid="funding-mix-horizon-label"
        >
          {horizon} Year Horizon
        </div>
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-[200px_1fr] gap-8 items-center content-center">
        <div style={{ position: "relative", height: 160 }} className="wf-nofocus">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={78}
                stroke="#fff"
                strokeWidth={2}
                startAngle={90}
                endAngle={-270}
                isAnimationActive={false}
                style={{ cursor: "default" }}
              >
                {data.map((d, i) => (
                  <Cell
                    key={i}
                    fill={d.fill}
                    data-testid={`donut-cell-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div
            className="absolute flex flex-col items-center justify-center pointer-events-auto transition-colors"
            data-testid="donut-center"
            style={{
              /* Only the inner hole is a click target so ring hovers pass through. */
              top: "50%",
              left: "50%",
              width: 96,
              height: 96,
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              cursor: "pointer",
              /* 2026-02-19 (Dave) — subtle navy tint on hover mirrors the
                 Waterfall / Elements(N) / Reserve Activity click-hint
                 pattern; replaces the previous dark-navy "click for
                 detail" tooltip that overlapped the Total value. */
              background: hovering ? "rgba(28, 78, 128, 0.06)" : "transparent",
            }}
            onClick={(e) => {
              hover.cancel();
              setActive(anchorAt(e));
            }}
            onMouseEnter={(e) => {
              setHovering(true);
              const at = anchorAt(e);
              hover.start(() => setActive(at));
            }}
            onMouseLeave={() => { setHovering(false); hover.cancel(); }}
            title="Hover or click for year-by-year total spending"
          >
            {/* Center always shows the Total — the hover state is now a
                subtle background tint, not a text-replacing tooltip. */}
            <div
              className="font-display text-[15px] text-navy leading-tight"
              style={{ fontWeight: 600 }}
              data-testid="donut-center-value"
            >
              {fmtCurrencyK(total)}
            </div>
            <div
              className="text-[9px] tracking-[0.1em] uppercase text-slate font-semibold"
              data-testid="donut-center-label"
            >
              Total
            </div>
          </div>
        </div>
        <div className="text-[10.5px]">
          <div className="flex items-center justify-between mb-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2" style={{ background: "#88BFDE" }} />
              <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-ink">
                Reserves
              </span>
            </span>
            <span
              className="font-mono tabular-nums text-ink font-semibold"
              data-testid="mix-reserves-pct"
            >
              {fmtPct(pctReserves, 0)}
            </span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2" style={{ background: "#EA6A47" }} />
              <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-ink">
                Special Assessments
              </span>
            </span>
            <span
              className="font-mono tabular-nums text-ink font-semibold"
              data-testid="mix-specials-pct"
            >
              {fmtPct(pctSpecials, 0)}
            </span>
          </div>
          <div className="flex items-center justify-between text-[10.5px]">
            <span className="text-ink uppercase tracking-wider">Special Assessments</span>
            <span
              className="font-mono tabular-nums text-coral font-semibold"
              data-testid="mix-specials-total"
            >
              {fmtCurrency(specials)}
            </span>
          </div>
          <div className="flex items-center justify-between text-[10.5px] mt-0.5">
            <span className="text-ink uppercase tracking-wider">Per Member</span>
            <span
              className="font-mono tabular-nums text-coral font-semibold"
              data-testid="mix-per-member"
            >
              {fmtCurrency(perMember)}
            </span>
          </div>
        </div>
      </div>
      {active && (
        <MixPopover
          segName={active.name}
          calc={calc}
          horizon={horizon}
          onClose={() => setActive(null)}
          anchor={{ x: active.x, y: active.y }}
        />
      )}
    </div>
  );
}
